import React, { useState } from 'react';
import { X, Target, CheckCircle, Star, Trophy, Award } from './Icons';

const DailyChallenge = ({ 
  isOpen, 
  onClose, 
  challenges, 
  userProgress, 
  language = 'traditional' 
}) => {
  const [selectedChallenge, setSelectedChallenge] = useState(null);

  if (!isOpen) return null;

  const getText = (textObj) => {
    if (typeof textObj === 'string') return textObj;
    return textObj[language] || textObj.traditional || textObj.english;
  };

  const completedChallenges = userProgress.dailyChallenges?.completed || [];
  const currentChallenge = userProgress.dailyChallenges?.current;

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white z-50 overflow-y-auto">
      <div className="flex justify-between items-center p-6">
        <h1 className="text-2xl font-bold">
          {language === 'traditional' ? '每日挑戰' :
           language === 'simplified' ? '每日挑战' :
           'Daily Challenge'}
        </h1>
        <button onClick={onClose}>
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Streak Counter */}
      <div className="px-6 mb-6">
        <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-6 text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <div className="text-3xl">🔥</div>
            <span className="text-3xl font-bold">{userProgress.statistics?.studyStreak || 0}</span>
          </div>
          <p className="text-lg font-semibold">
            {language === 'traditional' ? '天連擊' :
             language === 'simplified' ? '天连击' :
             'Day Streak'}
          </p>
          <p className="text-sm opacity-90">
            {language === 'traditional' ? '繼續保持學習動力！' :
             language === 'simplified' ? '继续保持学习动力！' :
             'Keep up the momentum!'}
          </p>
        </div>
      </div>

      {/* User Stats */}
      <div className="px-6 mb-6">
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white/10 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-blue-400">{userProgress.statistics?.totalLessonsCompleted || 0}</div>
            <div className="text-sm text-gray-300">
              {language === 'traditional' ? '完成課程' :
               language === 'simplified' ? '完成课程' :
               'Completed'}
            </div>
          </div>
          <div className="bg-white/10 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-green-400">{userProgress.statistics?.totalWordsLearned || 0}</div>
            <div className="text-sm text-gray-300">
              {language === 'traditional' ? '學會單詞' :
               language === 'simplified' ? '学会单词' :
               'Words Learned'}
            </div>
          </div>
          <div className="bg-white/10 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-purple-400">{userProgress.statistics?.averageScore || 0}%</div>
            <div className="text-sm text-gray-300">
              {language === 'traditional' ? '平均分數' :
               language === 'simplified' ? '平均分数' :
               'Average Score'}
            </div>
          </div>
        </div>
      </div>

      {/* Today's Challenges */}
      <div className="px-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">
          {language === 'traditional' ? '今日挑戰' :
           language === 'simplified' ? '今日挑战' :
           "Today's Challenges"}
        </h2>
        <div className="space-y-4">
          {challenges.map((challenge) => {
            const isCompleted = completedChallenges.some(c => c.id === challenge.id);
            const isCurrent = currentChallenge?.id === challenge.id;
            
            return (
              <div key={challenge.id} className="bg-white/10 rounded-xl p-4 flex items-center space-x-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  isCompleted ? 'bg-green-500' : isCurrent ? 'bg-blue-500' : 'bg-gray-600'
                }`}>
                  {isCompleted ? <CheckCircle className="w-6 h-6" /> : <Target className="w-6 h-6" />}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">{getText(challenge.title)}</h3>
                  <p className="text-gray-300 text-sm">{getText(challenge.description)}</p>
                  <div className="flex items-center space-x-2 mt-2">
                    <Star className="w-4 h-4 text-yellow-400" />
                    <span className="text-yellow-400 text-sm">{challenge.xp} XP</span>
                    <span className="text-gray-400 text-xs">
                      • {Math.floor(challenge.timeLimit / 60)}
                      {language === 'traditional' ? '分鐘' :
                       language === 'simplified' ? '分钟' :
                       ' min'}
                    </span>
                  </div>
                  {isCurrent && (
                    <div className="mt-2">
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${currentChallenge.progress || 0}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{currentChallenge.progress || 0}% 完成</p>
                    </div>
                  )}
                </div>
                {!isCompleted && !isCurrent && (
                  <button 
                    className="px-4 py-2 bg-blue-500 rounded-lg text-sm font-medium"
                    onClick={() => setSelectedChallenge(challenge)}
                  >
                    {language === 'traditional' ? '開始' :
                     language === 'simplified' ? '开始' :
                     'Start'}
                  </button>
                )}
                {isCurrent && (
                  <button className="px-4 py-2 bg-green-500 rounded-lg text-sm font-medium">
                    {language === 'traditional' ? '繼續' :
                     language === 'simplified' ? '继续' :
                     'Continue'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="px-6 mb-8">
        <div className="grid grid-cols-2 gap-4">
          <button className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl p-4 text-center">
            <Trophy className="w-6 h-6 mx-auto mb-2" />
            <span className="text-sm font-medium">
              {language === 'traditional' ? '排行榜' :
               language === 'simplified' ? '排行榜' :
               'Leaderboard'}
            </span>
          </button>
          <button className="bg-gradient-to-r from-green-500 to-blue-500 rounded-xl p-4 text-center">
            <Award className="w-6 h-6 mx-auto mb-2" />
            <span className="text-sm font-medium">
              {language === 'traditional' ? '成就' :
               language === 'simplified' ? '成就' :
               'Achievements'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DailyChallenge;
